/**
 * Fiskal Cloud Masterdata Import
 * # Overview Describes master data import functions of Fiskal Cloud. Supports import via CSV and structured import via JSON. The data import feature allows you to import your organizational structure from a file. The Fiskal Cloud supports three different import file formats. 1. A multi-level organization structure in a JSON format. 2. Single level files for organizations, business premises or ERS in JSON format. 3. Single level files for organizations, business premises or ERS in CSV format.  # Usage The import files can be uploaded to import-structured or import-file methods. All imports are processed asynchronously. The status and the results of an import can be checked via import-set methods. Each import is represented by an ImportSet that defines the metadata and the details for a single import. Only a single file can be uploaded at once. For an import of multiple files, please upload them one after the other in the correct order. Please note that you can only import business premises if the referenced parent organization is already known in the system and the same for ERS where the parent business premises need to be known. > Single import file should not be bigger than 1 MB in size. > In case you need to import bigger structures, please split them into separate files.  As a prerequisite for the data import, all elements need to have a unique external identifier that is used for identification of the element and referencing parent entities.  ## Getting an authentication token  To be able to work with the API you need to get an authentication token to use with all subsequent API calls described below. Get the token via the [Oauth endpoint](https://fiskal.cloud/auth/realms/df/protocol/openid-connect/token) of the Fiskal Cloud. Use client_id=df-api and grant_type=password for requesting a token. > The access token grants same access rights as the user has in the Fiskal Cloud Portal.  Example request to get token via curl: ``` curl -X POST \\ https://fiskal.cloud/auth/realms/df/protocol/openid-connect/token \\ -H \'Accept: application/json\' \\ -H \'Content-Type: application/x-www-form-urlencoded\' \\ -d \'username=<USER_EMAILADDRESS>&password=<USER_PASSWORD>&client_id=df-api&grant_type=password\' ``` Save the access_token entry of the response and pass it as bearer token in all subsequent API calls. Pass it as header parameter. ```\'Authorization: Bearer <ACCESS_TOKEN_FROM_AUTHENTICATION>\'```  For details on access tokens, please check general Oauth documentation like [Oauth.com](https://www.oauth.com/oauth2-servers/access-tokens/access-token-response/)  ### Managed Tenant You can use a different tenant for operations if you have a permission to manage them. You can select a managed tenant by sending `X-Managed-Tenant` header with `tenantId` of the managed tenant. All operations (creating, updating and getting) are done in the context of the managed tenant with this header.  ``` curl -X GET \\ -i \'https://fiskal.cloud/api/import/imports\' \\ -H \'Authorization: Bearer {{AccessToken}}\' \\ -H \'X-Managed-Tenant: {{managedTenantId}}\' ```  ### Empty string `\"\"` behaviour change Currently our API converts all empty string `\"\"` values to null. This behaviour does not correspond to this OpenAPI specification. This behaviour **will be changed** in the near future and our API will not handle empty strings as nulls. Therefore your empty string values can be handled as invalid values. We strongly recommend not sending empty string values.  # Changelog  ## 1.6.0 * Remove FCR imports  ## 1.5.9 * Set external virtual TSS field `deviceType` as optional  ## 1.5.8 * Deprecated field `economicIdentificationNumber` in organization * Added new field `businessIdentificationNumber` to organization  ## 1.5.7 * Create external ERS without assigned external virtual TSS  ## 1.5.6 * Remove External VirtualTSS `activationDate` * Add ERS `commissioningDate` * Rename External ERS `activationDate` to `commissioningDate` * Add External ERS `tssConnectionDate` (`TSE_INBETRIEBNAHME`) * ERS `acquisitionDate` cannot be in the future * Add ERS `Z_KASSE_ANSCHAFFUNG` * ERS `Z_KASSE_INBETRIEBNAHME` is mapped to `commissioningDate` * Rename `extVirtualTssChildren` to `extTssChildren`  ## 1.5.5 * Remove ERS `deviceTypeAdditional` field * Remove VirtualTss `deviceTypeAdditional` and `bsiValidUntil`  ## 1.5.4 * Import external entities (ERS, TSS)  ## 1.5.3 * Rename ERS field in JSON format from `deviceTypeOther` to `deviceTypeAdditional` * Rename ERS column in CSV format from `Z_DEVICE_TYPE_OTHER` to `Z_DEVICE_TYPE_ADDITIONAL`  ## 1.5.2 * Unify validations with other APIs * Empty string `\"\"` as a value will not be handled as `null` announcement  ## 1.5.1 * Extend model to support Abgabenordnung § 146   * Unify identificationNumber   * Fix houseNumber validation  ## 1.5.0 * Extend model to support Abgabenordnung § 146 ## 1.4.0 * Add an option to assign FCRs through import ## 1.3.0 * Add created/modified user IDs * Add ApiErrorResponse 
 *
 * The version of the OpenAPI document: 1.5.9
 * Contact: info@deutsche-fiskal.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent, HttpParameterCodec, HttpContext 
        }       from '@angular/common/http';
import { CustomHttpParameterCodec }                          from '../encoder';
import { Observable }                                        from 'rxjs';

// @ts-ignore
import { ImportOptionsDTO } from '../model/import-options-dto';
// @ts-ignore
import { ImportSetDTO } from '../model/import-set-dto';

// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';
import {
    ImportStructuredApiClientInterface,
    ImportStructureRequestParams
} from './import-structured-api-client.serviceInterface';



@Injectable({
  providedIn: 'root'
})
export class ImportStructuredApiClient implements ImportStructuredApiClientInterface {

    protected basePath = 'https://fiskal.cloud/api/import';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    public encoder: HttpParameterCodec;

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string|string[], @Optional() configuration: Configuration) {
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (Array.isArray(basePath) && basePath.length > 0) {
                basePath = basePath[0];
            }

            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }

    /**
     * @param consumes string[] mime-types
     * @return true: consumes contains 'multipart/form-data', false: otherwise
     */
    private canConsumeForm(consumes: string[]): boolean {
        const form = 'multipart/form-data';
        for (const consume of consumes) {
            if (form === consume) {
                return true;
            }
        }
        return false;
    }

    // @ts-ignore
    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }

    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                (value as any[]).forEach( elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key, (value as Date).toISOString().substr(0, 10));
                } else {
                   throw Error("key may not be null if value is Date");
                }
            } else {
                Object.keys(value).forEach( k => httpParams = this.addToHttpParamsRecursive(
                    httpParams, value[k], key != null ? `${key}.${k}` : k));
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value);
        } else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }

    /**
     * Import organization master data in structured JSON format.
     * This function will import an organizational structure in JSON Format. The import will match elements by their external identifiers and update existing or add new ones. Setting the status of an element to disabled or deleted will disable respectively delete an existing entity. Additional restrictions may apply and such status change may not be possible. Please check the development portal for detailed instructions. New entities with disabled or deleted status will be skipped and not be imported. It is also possible to only import parts of the organizational structure. All imports are cumulative. 
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public importStructure(requestParameters: ImportStructureRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<ImportSetDTO>;
    public importStructure(requestParameters: ImportStructureRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<ImportSetDTO>>;
    public importStructure(requestParameters: ImportStructureRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<ImportSetDTO>>;
    public importStructure(requestParameters: ImportStructureRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const importOptions = requestParameters.importOptions;
        if (importOptions === null || importOptions === undefined) {
            throw new Error('Required parameter importOptions was null or undefined when calling importStructure.');
        }
        const file = requestParameters.file;
        if (file === null || file === undefined) {
            throw new Error('Required parameter file was null or undefined when calling importStructure.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'multipart/form-data'
        ];

        const canConsumeForm = this.canConsumeForm(consumes);

        let localVarFormParams: { append(param: string, value: any): any; };
        let localVarUseForm = false;
        let localVarConvertFormParamsToString = false;
        // use FormData to transmit files using content-type "multipart/form-data"
        // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
        localVarUseForm = canConsumeForm;
        if (localVarUseForm) {
            localVarFormParams = new FormData();
        } else {
            localVarFormParams = new HttpParams({encoder: this.encoder});
        }

        if (importOptions !== undefined) {
            localVarFormParams = localVarFormParams.append('importOptions', localVarUseForm ? new Blob([JSON.stringify(importOptions)], {type: 'application/json'}) : <any>importOptions) as any || localVarFormParams;
        }
        if (file !== undefined) {
            localVarFormParams = localVarFormParams.append('file', <any>file) as any || localVarFormParams;
        }

        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/imports/structure`;
        return this.httpClient.request<ImportSetDTO>('post', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
